import React from 'react';
import styles from './CustomerDetailsSummary.scss';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';
import { translate } from 'react-i18next';
import PhoneWithDot from './PhoneWithDot';
import RotatableChevron from '../RotatableChevron';
import { SPECIAL_KEY_SYMBOL } from '../../utils/enviormentUtils';
import { Text, Tooltip } from 'wix-style-react';
import { DeliveryTypes } from '../../utils/constants';

const PLACEHOLDER = '-';

function getOnArrivalString(onArrival = '') {
  const str = onArrival.toLowerCase();
  return str === 'call' ? 'phone' : str;
}

export function CustomerDetailsSummary(props) {
  function handleHeaderClick(e) {
    e.stopPropagation();
    props.onHeaderClick(e);
  }

  const { t, displayChevron, shouldDisplayOrderHistory } = props;
  const customerInfoTooltip = `${t('phoneordering_keyboardShortcut_customerinfo_tooltip')} (${SPECIAL_KEY_SYMBOL} + I)`;

  return (
    <div className={classNames([styles.wrapper, props.className])} data-hook="customer-details-summary-wrapper">
      <Tooltip size={'small'} content={customerInfoTooltip} enterDelay={500} appendTo={'window'}>
        <div className={styles.header} onClick={handleHeaderClick}>
          <PhoneWithDot />
          <Text
            size="medium"
            weight="normal"
            className={styles.headerText}
            ellipsis
            data-hook="customer-details-summary-name"
          >
            {props.name.split(' ')[0]}
          </Text>
          {displayChevron && <RotatableChevron up={!props.isExpanded} />}
        </div>
      </Tooltip>

      <div className={styles.fullDivider} />

      <div className={styles.group} data-hook="customer-info" onClick={props.onCustomerInfoClick}>
        <Text size="medium" weight="bold" tagName="div" className={styles.title} data-hook="customer-info-title">
          {t('phoneordering_menu_customerinfo_label')}
        </Text>
        <div className={styles.item} data-hook="name-wrapper">
          <Text size="small" weight="normal" light secondary tagName="div" data-hook="name-subtitle">
            {t('phoneordering_menu_customerinfo_name_label')}
          </Text>
          <Text size="medium" weight="normal" tagName="div" data-hook="name" ellipsis>
            {props.name}
          </Text>
        </div>
        <div className={styles.item} data-hook="phone-number-wrapper">
          <Text size="small" weight="normal" light secondary tagName="div" data-hook="phone-number-subtitle">
            {t('phoneordering_menu_customerinfo_phone_label')}
          </Text>
          <Text size="medium" weight="normal" tagName="div" data-hook="phone-number">
            {props.phoneNumber}
          </Text>
        </div>
        {props.email && (
          <div className={styles.item} data-hook="email-address-wrapper">
            <Text size="small" weight="normal" light secondary tagName="div" data-hook="email-address-subtitle">
              {t('phoneordering_menu_customerinfo_email_label')}
            </Text>
            <Text size="medium" weight="normal" tagName="div" data-hook="email" ellipsis>
              {props.email}
            </Text>
          </div>
        )}
      </div>

      <div className={styles.divider} />

      <div className={styles.group} data-hook="address-details" onClick={props.onAddressDetailsClick}>
        <Text size="medium" weight="bold" tagName="div" className={styles.title} data-hook="address-details-title">
          {props.deliveryType === 'delivery'
            ? t('phoneordering_menu_customerinfo_delivery_label')
            : t('phoneordering_menu_customerinfo_pickup_label')}
        </Text>

        {props.deliveryType === 'delivery' && (
          <div className={styles.item} data-hook="address-details-wrapper">
            <Text size="small" weight="normal" light secondary tagName="div" data-hook="address-details-subtitle">
              {t('phoneordering_menu_customerinfo_deliveryaddress_label')}
            </Text>
            <Text size="medium" weight="normal" tagName="div" data-hook="address-details-delivery-to">
              {props.address}
            </Text>
          </div>
        )}
        {props.deliveryType === 'takeout' && (
          <div className={styles.item} data-hook="address-details-pickup-from-wrapper">
            <Text
              size="small"
              weight="normal"
              light
              secondary
              tagName="div"
              data-hook="address-details-pickup-from-subtitle"
            >
              {t('phoneordering_customerinfo_ordertype_address_pickup_from')}
            </Text>
            <Text size="medium" weight="normal" tagName="div" data-hook="pickup-from">
              {props.organizationAddress}
            </Text>
          </div>
        )}
        {props.deliveryType === 'delivery' && Boolean(props.apt || props.floor || props.entrance) && (
          <div className={classNames([styles.item, styles.horizontalGroup])} data-hook="address-meta-data">
            <div className={styles.horizontalItem} data-hook="address-meta-data-apt-wrapper">
              <Text
                size="small"
                weight="normal"
                light
                secondary
                tagName="div"
                data-hook="address-meta-data-apt-subtitle"
              >
                {t('phoneordering_menu_customerinfo_apartment_label')}
              </Text>
              <Text size="medium" weight="normal" tagName="div" data-hook="address-meta-data-apt">
                {props.apt || PLACEHOLDER}
              </Text>
            </div>

            <div className={styles.horizontalItem} data-hook="address-meta-data-floor-wrapper">
              <Text
                size="small"
                weight="normal"
                light
                secondary
                tagName="div"
                data-hook="address-meta-data-floor-subtitle"
              >
                {t('phoneordering_menu_customerinfo_floor_label')}
              </Text>
              <Text size="medium" weight="normal" tagName="div" data-hook="address-meta-data-floor">
                {props.floor || PLACEHOLDER}
              </Text>
            </div>
            <div className={styles.horizontalItem} data-hook="address-meta-data-entrance-wrapper">
              <Text
                size="small"
                weight="normal"
                light
                secondary
                tagName="div"
                data-hook="address-meta-data-entrance-subtitle"
              >
                {t('phoneordering_menu_customerinfo_entrance_label')}
              </Text>
              <Text size="medium" weight="normal" tagName="div" data-hook="address-meta-data-entrance">
                {props.entrance || PLACEHOLDER}
              </Text>
            </div>
          </div>
        )}
        {props.deliveryType === 'delivery' && props.onArrival && (
          <div className={styles.item} data-hook="customer-details-on-arrival-wrapper">
            <Text
              size="small"
              weight="normal"
              light
              secondary
              tagName="div"
              data-hook="customer-details-on-arrival-subtitle"
            >
              {t('phoneordering_menu_customerinfo_onarrival_label')}
            </Text>
            <Text size="medium" weight="normal" tagName="div" data-hook="customer-details-on-arrival">
              {t(`phoneordering_menu_customerinfo_onarrival_${getOnArrivalString(props.onArrival)}`)}
            </Text>
          </div>
        )}
        {props.deliveryType === 'delivery' && props.instructions && (
          <div className={styles.item} data-hook="customer-details-instructions-wrapper">
            <Text
              size="small"
              weight="normal"
              light
              secondary
              tagName="div"
              data-hook="customer-details-instructions-subtitle"
            >
              {t('phoneordering_menu_customerinfo_deliveryinstructions_label')}
            </Text>
            <Text size="medium" weight="normal" tagName="div" ellipsis data-hook="customer-details-instructions">
              {props.instructions}
            </Text>
          </div>
        )}
        <div className={styles.item} data-hook="customer-details-deliver-by-wrapper">
          <Text
            size="small"
            weight="normal"
            light
            secondary
            tagName="div"
            data-hook="customer-details-deliver-by-subtitle"
          >
            {props.deliveryType === DeliveryTypes.DELIVERY
              ? t('phoneordering_menu_customerinfo_time_label')
              : t('phoneordering_customerinfo_ordertype_address_pickup_by')}
          </Text>
          <Text
            size="medium"
            weight="normal"
            tagName="div"
            data-hook="deliver-by"
            data-hook="customer-details-deliver-by"
          >
            {props.deliverBy}
          </Text>
        </div>
      </div>

      <div className={styles.divider} />

      {shouldDisplayOrderHistory && (
        <div className={styles.group} data-hook="customer-details-order-history" onClick={props.onOrderHistoryClick}>
          <Text size="medium" weight="bold" tagName="div" className={styles.title}>
            {t('phoneordering_menu_customerinfo_orderhistory_label')}
          </Text>
        </div>
      )}
    </div>
  );
}

CustomerDetailsSummary.displayName = 'CustomerDetailsSummary';
CustomerDetailsSummary.propTypes = {
  name: string,
  phoneNumber: string,
  email: string,
  address: string,
  apt: string,
  floor: string,
  entrance: string,
  onArrival: string,
  instructions: string,
  deliverBy: string,
  deliveryType: string,
  className: string,
  onHeaderClick: func,
  isExpanded: bool,
  onCustomerInfoClick: func,
  onAddressDetailsClick: func,
  onOrderHistoryClick: func,
  t: func.isRequired,
  organizationAddress: string,
  displayChevron: bool,
  shouldDisplayOrderHistory: bool,
};
CustomerDetailsSummary.defaultProps = {
  name: '',
  displayChevron: true,
};

export default translate()(CustomerDetailsSummary);
